export const REDACTED_LOG_VALUE = '<redacted>';
const MAX_RECURSION_DEPTH = 6;
const MAX_LOG_STRING_LENGTH = 4096;
const CIRCULAR_REFERENCE_LOG_VALUE = '<circular-reference>';
const MAX_DEPTH_LOG_VALUE = '<max-depth-exceeded>';

const SENSITIVE_KEY_TOKENS = [
    'authorization',
    'apikey',
    'token',
    'secret',
    'password',
    'cookie',
    'session',
    'jwt',
    'bearer',
    'credentials',
    'privatekey',
    'payload',
    'body',
    'requestdata',
    'responsedata',
];

const redactStringByPattern = (value: string): string => {
    const withoutBearer = value
        .replace(/(Bearer\s+)[A-Za-z0-9\-._~+/=]+/gi, `$1${REDACTED_LOG_VALUE}`)
        .replace(/(Basic\s+)[A-Za-z0-9\-._~+/=]+/gi, `$1${REDACTED_LOG_VALUE}`);

    return withoutBearer.replace(
        /([?&](?:access[_-]?token|refresh[_-]?token|api[_-]?key|apikey|token|secret|password|authorization)=)[^&#\s]*/gi,
        `$1${REDACTED_LOG_VALUE}`,
    );
};

const truncateLogString = (value: string): string => {
    if (value.length <= MAX_LOG_STRING_LENGTH) {
        return value;
    }

    return `${value.slice(0, MAX_LOG_STRING_LENGTH)}...<truncated>`;
};

const normalizeLogKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

export const isSensitiveLogKey = (key: string): boolean => {
    const normalized = normalizeLogKey(key);
    return SENSITIVE_KEY_TOKENS.some((token) => normalized.includes(token));
};

const sanitizeInternal = (
    value: unknown,
    options: {
        depth: number;
        seen: WeakSet<object>;
        redactSensitiveKeys: boolean;
    },
    keyName?: string,
): unknown => {
    if (keyName && options.redactSensitiveKeys && isSensitiveLogKey(keyName)) {
        return REDACTED_LOG_VALUE;
    }

    if (value === null || value === undefined) {
        return value;
    }

    if (typeof value === 'string') {
        return truncateLogString(redactStringByPattern(value));
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'bigint') {
        return value.toString();
    }

    if (typeof value === 'symbol') {
        return value.toString();
    }

    if (typeof value === 'function') {
        return `[function ${value.name || 'anonymous'}]`;
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (value instanceof Error) {
        return {
            name: value.name,
            message: truncateLogString(redactStringByPattern(value.message)),
        };
    }

    if (Array.isArray(value)) {
        if (options.depth >= MAX_RECURSION_DEPTH) {
            return MAX_DEPTH_LOG_VALUE;
        }

        return value.map((entry) =>
            sanitizeInternal(entry, {
                ...options,
                depth: options.depth + 1,
            }),
        );
    }

    if (typeof value === 'object') {
        if (options.depth >= MAX_RECURSION_DEPTH) {
            return MAX_DEPTH_LOG_VALUE;
        }

        const objectValue = value as Record<string, unknown>;
        if (options.seen.has(objectValue)) {
            return CIRCULAR_REFERENCE_LOG_VALUE;
        }

        options.seen.add(objectValue);

        const sanitizedRecord: Record<string, unknown> = {};
        for (const [key, entryValue] of Object.entries(objectValue)) {
            sanitizedRecord[key] = sanitizeInternal(
                entryValue,
                {
                    ...options,
                    depth: options.depth + 1,
                },
                key,
            );
        }

        return sanitizedRecord;
    }

    return truncateLogString(redactStringByPattern(String(value)));
};

export const sanitizeForLog = (
    value: unknown,
    options: {
        redactSensitiveKeys?: boolean;
    } = {},
): unknown => {
    return sanitizeInternal(
        value,
        {
            depth: 0,
            seen: new WeakSet<object>(),
            redactSensitiveKeys: options.redactSensitiveKeys ?? true,
        },
        undefined,
    );
};

export const redactSecretForLog = (value: string | null | undefined): string => {
    if (!value || !value.trim()) {
        return 'empty';
    }

    return REDACTED_LOG_VALUE;
};

export const stringifyForLog = (value: unknown): string => {
    try {
        return JSON.stringify(value);
    } catch {
        return JSON.stringify({
            message: 'failed_to_stringify_log_payload',
        });
    }
};
